import React, { useState, useEffect, useMemo } from 'react';
import store, { templatesAction } from '@apply/adminStore';
import { CustomStore, InformModal } from '../../common/SharedComponents';
import { BfiDataGrid, Column } from '../../common/SharedComponents';
import { FileApi, TemplatesApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { GridSettingType } from '@apply/constants';
import VisibilityIcon from '@material-ui/icons/VisibilityTwoTone';
import { formatDate, handleAdminInfo, handleAdminSuccess } from '../../common/SharedFunctions';
import { ActionsButton, PreviewModal } from '../common/TemplateComponents';
import LoadPanel from 'devextreme-react/load-panel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleDown, faFileExcel, faSpinner } from '@fortawesome/free-solid-svg-icons';

//Put into seperate component so that Grid doesn't reload on Click
const PreviewLink = ({ id }) => {
    const [isLoading, set_isLoading] = useState(false);
    const [openPreviewModal, set_openPreviewModal] = useState(false);
    const [previewData, set_previewData] = useState('');

    const templatesApi = new TemplatesApi.TemplatesApi(applicationInsights);

    const onPreviewClick = async (templateId) => {
        try {
            set_isLoading(true);
            const response = await templatesApi.previewTemplate(templateId);
            set_previewData(response.result);
            set_openPreviewModal(true);
        } finally {
            set_isLoading(false);
        }
    };

    return (
        <>
            <div title='Click to preview template'>
                <VisibilityIcon
                    className={'color-blue-medium'}
                    onClick={() => onPreviewClick(id)}
                />
            </div>
            <PreviewModal
                isOpen={openPreviewModal}
                onClose={() => {
                    set_openPreviewModal(false);
                }}
                previewHtml={previewData.html}
                templateTypeId={previewData.templateTypeId}
                altContact={previewData.altContact}
                altCompany={previewData.altCompany}
                altAddress={previewData.altAddress}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={isLoading}
                message='Loading...'
            />
        </>
    );
};

export const TemplatesTable = ({ templateTypeId }) => {
    const { dispatch } = store;
    const templatesApi = new TemplatesApi.TemplatesApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);

    const [isLoading, set_isLoading] = useState(false);
    const [informModalOpen, set_informModalOpen] = useState(false);
    const [informModalText, set_informModalText] = useState(false);

    const customStore = useMemo(
        () =>
            new CustomStore({
                key: 'id',
                load: async () => {
                    const response = await templatesApi.getTemplatesGrid(templateTypeId);
                    if (response.statusCode !== 200) {
                        return;
                    }

                    var rows = response.result.data;

                    rows.forEach((row) => {
                        if (row.filePubSubConnectionUrl) {
                            const ws = new WebSocket(row.filePubSubConnectionUrl);
                            ws.onopen = () => {
                                handleAdminInfo('Generating PDF Preview...');
                            };

                            ws.onmessage = (event) => {
                                row.filePubSubConnectionUrl = null;
                                row.fileDownloadToken = event.data;

                                handleAdminSuccess('PDF Preview Ready');
                            };
                        }
                    });

                    return response.result;
                },
            }),
        [templateTypeId]
    );

    const onCellDblClick = (e) => {
        dispatch(
            templatesAction.setEditTemplateModal({
                editTemplateModal: { isOpen: true, templateId: e.data.id },
            })
        );
    };

    const onGeneratePdfClick = async (versionId) => {
        try {
            set_isLoading(true);
            const response = await templatesApi.generatePdf(versionId);
            if (response?.statusCode === 200) {
                customStore.load();
            } else {
                set_informModalText('The system was not able to process this PDF. Please contact support.');
                set_informModalOpen(true);
            }
        } catch {
            set_informModalText('The system was not able to process this PDF. Please contact support.');
            set_informModalOpen(true);
        } finally {
            set_isLoading(false);
        }
    };

    const onDownloadClick = async (token) => {
        const response = await fileApi.downloadUrl({ token });
        if (response.statusCode !== 200) return;
        window.location.href = response.result.downloadUrl;
    };

    const onPreviewRender = (e) => {
        if (!e.data.generatesPdf) {
            return 'NA';
        }
        return <PreviewLink id={e.data.id} />;
    };

    const onDownloadRender = (e) => {
        if (!e.data.generatesPdf) {
            return 'NA';
        }

        if (e.data.fileDownloadToken && !e.data.filePubSubConnectionUrl) {
            return (
                <button
                    className='gridButton'
                    onClick={() => onDownloadClick(e.data.fileDownloadToken)}
                    title='Click to download PDF'
                >
                    <FontAwesomeIcon icon={faArrowCircleDown} /> Download
                </button>
            );
        } else if (e.data.filePubSubConnectionUrl) {
            return (
                <span
                    style={{
                        color: 'black',
                        width: '90px',
                        textAlign: 'center',
                        display: 'inline-block',
                    }}
                >
                    <FontAwesomeIcon
                        icon={faSpinner}
                        title='PDF is generating...'
                    />
                </span>
            );
        } else if (!e.data.fileDownloadToken && !e.data.filePubSubConnectionUrl) {
            return (
                <button
                    className='gridButton red-text'
                    onClick={() => onGeneratePdfClick(e.data.latestVersionId)}
                    title='Click to generate template PDF'
                >
                    <FontAwesomeIcon icon={faFileExcel} /> Create PDF
                </button>
            );
        } else {
            return null;
        }
    };

    const onRenderDate = (e) => {
        if (!e.value) {
            return null;
        }
        return formatDate(e.value);
    };

    const onActionsCellRender = (e) => {
        return <ActionsButton data={e.data} />;
    };

    return (
        <>
            <BfiDataGrid
                id={'borrowerEntitiesGrid'}
                gridType={GridSettingType.Templates}
                dataStore={customStore}
                applicationInsights={applicationInsights}
                onCellDblClick={onCellDblClick}
            >
                <Column
                    dataField='name'
                    caption='Template Name'
                    width={400}
                />
                <Column
                    dataField='versionName'
                    caption='Version'
                    width={120}
                />
                <Column
                    dataField='editedBy'
                    caption='Edited By'
                    width={200}
                />
                <Column
                    dataField='editedDate'
                    caption='Last Edited'
                    width={100}
                    cellRender={onRenderDate}
                />
                <Column
                    caption='Preview'
                    width={80}
                    cellRender={onPreviewRender}
                    alignment='center'
                />
                <Column
                    caption='PDF'
                    width={110}
                    cellRender={onDownloadRender}
                    alignment='center'
                />
                <Column
                    dataField='notes'
                    caption='Notes'
                />
                <Column
                    type='actions'
                    alignment='center'
                    cellRender={(e) => onActionsCellRender(e)}
                    width={50}
                    allowReordering={false}
                    allowSorting={false}
                    allowResizing={false}
                    fixed={true}
                    fixedPosition={'right'}
                />
            </BfiDataGrid>
            <InformModal
                isOpen={informModalOpen}
                text={informModalText}
                confirmCallback={() => set_informModalOpen(false)}
            />
            <LoadPanel
                hideOnOutsideClick={false}
                visible={isLoading}
                message='Loading...'
            />
        </>
    );
};
